// crewbit-admin/src/components/money/VendorBillPanel.tsx
//
// §Money.StubActionSweepCompletion Phase 3. Centered card modal for the
// vendor-invoice HeaderQuickActions verb. Calls existing `create_vendor_bill`
// SECDEF RPC (no new substrate). Free-text vendor name v1; "+ Add vendor"
// button opens AddVendorPanel inline.

import { useState } from 'react';
import { FileText } from 'lucide-react';
import { palette, spacing, transition, typography } from '../../styles/tokens';
import { MoneyInput } from '../common/MoneyInput';
import { canonicalMoney, parseMoney, isPositive } from '../../lib/money';
import { createVendorBill } from '../../lib/vendorBillMutations';
import { AddVendorPanel } from './AddVendorPanel';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function VendorBillPanel({ isOpen, onClose, onResult }: Props) {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);

  // Idempotency key per form mount; replay is a no-op server-side.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [vendorName, setVendorName] = useState('');
  const [amount, setAmount] = useState(canonicalMoney(0));
  const [hstAmount, setHstAmount] = useState(canonicalMoney(0));
  const [billDate, setBillDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);

  if (!isOpen) return null;

  const valid = vendorName.trim().length > 0 && isPositive(parseMoney(amount));

  const reset = () => {
    setVendorName('');
    setAmount(canonicalMoney(0));
    setHstAmount(canonicalMoney(0));
    setBillDate(todayIso());
    setDueDate('');
    setDescription('');
    setIdempotencyKey(crypto.randomUUID());
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    if (!valid || !businessId) return;
    setLoading(true);
    const result = await createVendorBill({
      businessId,
      vendorName: vendorName.trim(),
      amount: Number(parseMoney(amount)),
      hstAmount: Number(parseMoney(hstAmount)) || null,
      billDate,
      dueDate: dueDate || null,
      description: description.trim() || null,
      idempotencyKey,
    });
    setLoading(false);
    if (result.success) {
      onResult(true, result.data.already_created
        ? 'Bill already recorded.'
        : `Bill from ${vendorName.trim()} recorded.`);
      reset();
      onClose();
    } else {
      onResult(false, result.error.message ?? 'Failed to record vendor bill.');
    }
  };

  return (
    <>
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 480, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <FileText size={22} color={palette.accent} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Record a vendor bill
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Logs a bill you owe to a supplier. Mark it paid later from the Vendor bills list.
        </div>

        <div style={{ display: 'grid', gap: spacing.sm }}>
          <Field label="Vendor name" required>
            <div style={{ display: 'flex', gap: spacing.xs }}>
              <input
                type="text"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value.slice(0, 200))}
                placeholder="e.g. Home Depot"
                style={{ ...inputStyle, flex: 1 }}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setAddVendorOpen(true)}
                style={{
                  padding: '0 14px', borderRadius: 8,
                  border: `1px solid ${palette.g200}`, background: palette.white,
                  fontSize: 12, fontWeight: 500, color: palette.accent,
                  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}
              >+ New vendor</button>
            </div>
          </Field>

          <Field label="Amount" required>
            <MoneyInput value={amount} onChange={setAmount} ariaLabel="Bill amount" />
          </Field>

          <Field label="HST amount">
            <MoneyInput value={hstAmount} onChange={setHstAmount} ariaLabel="HST amount" />
          </Field>

          <Field label="Bill date">
            <input
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
              style={inputStyle}
            />
          </Field>

          <Field label="Due date">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </Field>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Optional"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={handleClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !valid} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !valid) ? palette.g300 : palette.accent,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Recording…' : 'Record bill'}</button>
        </div>
      </div>

      <AddVendorPanel
        isOpen={addVendorOpen}
        onClose={() => setAddVendorOpen(false)}
        onResult={onResult}
        onSuccess={(_vendorId) => {
          // Pre-fill the vendor_name field from the just-created vendor (operator
          // typed the name into AddVendor, we can copy the canonical version).
          // For v1 we just leave the user's typed name untouched.
        }}
      />
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: spacing.sm, borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
  fontSize: typography.size.caption, color: palette.g800,
  boxSizing: 'border-box', background: palette.white,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
        {label}{required && <span style={{ color: palette.red, marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </label>
  );
}
